import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('[ErrorBoundary] Caught error:', error.message, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-4 space-y-6 animate-fade-in">
          <div className="tg-card flex flex-col items-center py-10 text-center">
            <span className="text-5xl mb-4">😵</span>
            <h2 className="text-lg font-bold text-[var(--tg-theme-text-color)] mb-2">
              Что-то пошло не так
            </h2>
            <p className="text-sm text-[var(--tg-theme-hint-color)] mb-6">
              Произошла ошибка при загрузке этой страницы.
            </p>
            {/* Error message for debugging */}
            {this.state.error && (
              <details className="w-full mb-4">
                <summary className="text-xs text-[var(--tg-theme-hint-color)] cursor-pointer hover:text-[var(--tg-theme-text-color)] transition-colors">
                  Техническая информация
                </summary>
                <pre className="mt-2 text-[10px] text-red-500 bg-[var(--tg-theme-secondary-bg-color)] p-2 rounded-xl text-left overflow-auto max-h-24 font-mono">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="tg-button text-sm"
            >
              🔄 Попробовать снова
            </button>
            <button
              onClick={() => {
                try { localStorage.removeItem('edupulse_theme'); } catch {}
                window.location.reload();
              }}
              className="text-xs text-[var(--tg-theme-hint-color)] mt-3 underline hover:text-[var(--tg-theme-text-color)] transition-colors"
            >
              ⚠️ Сбросить тему и перезагрузить
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
